import { observer } from "mobx-react-lite";
import MainLayout, { MAIN_RIGHT_TOP_HEIGHT } from "../MainLayout";
import { useNavigate } from "react-router-dom";
import { VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium } from "../../common/common-text";
import { useEffect, useState } from "react";
import { fetchRecruitList, fetchRecruitType, requestDeleteAllFiles, requestDeleteRecruitDocuments } from "../../service/BoardService";
import BTBox from "../../common/components/BTBox";
import BTList from "../../common/components/board/BTList";
import { GridRowParams } from "@mui/x-data-grid";
import Recruit from "../../model/board/Recruit";
import { formatDateToYMD } from "../../common/utils/DateUtils";
import { dialogStore } from "../../store/DialogStore";

const RecruitBoard = () => {
    return (
        <MainLayout>
            <RecruitBoardBody />
        </MainLayout>
    );
};

export default RecruitBoard;

const RecruitBoardBody = observer(() => {
    const navigate = useNavigate();
    const [recruits, setRecruits] = useState<Recruit[]>([]);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [recruitTypeOptions, setRecruitTypeOptions] = useState<{ label: string; value: string }[]>([]);
    const [selectedJobType, setSelectedJobType] = useState<string>('all');

    const loadData = async () => {
        const result = await fetchRecruitList();
        setRecruits(result);
        
        const types = await fetchRecruitType();
        setRecruitTypeOptions(types.map(tab => ({
            label: tab.tabName,
            value: tab.tabCode
        })));
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleWriteClick = () => {
        navigate("/board/recruit/add", {
            state: recruitTypeOptions
        });
    };

    const handleDeleteClick = async (selectedIndexes: number[]) => {
        if (selectedIndexes.length === 0) {
            dialogStore.showDialog({
                title: '알림',
                message: '삭제할 아이템을 선택해주세요.',
                onConfirm: () => {},
                onClose: () => {},
            });
            return;
        }

        const deleteConfirmed = await dialogStore.showAsyncDialog({
            title: '경고',
            message: `선택된 ${selectedIndexes.length}개의 채용공고를 정말로 삭제하시겠습니까?`,
            isCaution: true,
        });

        if (!deleteConfirmed) return;

        try {
            const selectedRecruitIds: string[] = selectedIndexes
                .map((idx) => recruits[idx]?.id)
                .filter((id): id is string => !!id);

            await requestDeleteRecruitDocuments(selectedRecruitIds);
            await Promise.all(selectedRecruitIds.map((docId) => requestDeleteAllFiles("recruit", docId)));

            dialogStore.showDialog({
                title: '삭제 완료',
                message: `${selectedRecruitIds.length}개의 채용공고가 삭제되었습니다.`,
                onConfirm: () => { setSelectedRows([]); loadData(); },
                onClose: () => { setSelectedRows([]); loadData(); },
            });
        } catch (e) {
            console.error('채용공고 삭제 실패', e);
            dialogStore.showDialog({
                title: '오류',
                message: `채용공고 삭제 중 오류가 발생했습니다.`,
                onConfirm: () => {},
                onClose: () => {},
            });
        }
    };

    const columns = [
        { field: 'id', headerName: '번호', width: 80, headerAlign: 'center', align: 'center' },
        { field: 'jobTitle', headerName: '제목', flex: 1, headerAlign: 'center', align: 'left' },
        { field: 'jobType', headerName: '채용 형태', width: 120, headerAlign: 'center', align: 'center' },
        { field: 'recruitStartDate', headerName: '시작일', width: 110, headerAlign: 'center', align: 'center' },
        { field: 'recruitEndDate', headerName: '종료일', width: 110, headerAlign: 'center', align: 'center' },
        { field: 'recruitStatus', headerName: '상태', width: 100, headerAlign: 'center', align: 'center' },
        { field: 'isPublished', headerName: '공개 여부', width: 120, headerAlign: 'center', align: 'center' },
    ];

    const today = new Date();
    const filteredRecruits = selectedJobType === 'all'
        ? recruits
        : recruits.filter((recruit) => recruit.jobType === selectedJobType);

    const rows = filteredRecruits.map((recruit, index) => {
        const startDate = new Date(recruit.recruitStartDate);
        const endDate = recruit.recruitEndDate ? new Date(recruit.recruitEndDate) : null;
        const jobTypeLabel = recruitTypeOptions.find((option) => option.value === recruit.jobType)?.label ?? '-';
        const isOngoing = startDate <= today && (endDate === null || endDate >= today);

        return {
            id: filteredRecruits.length - index,
            jobTitle: recruit.jobTitle,
            jobType: jobTypeLabel,
            recruitStartDate: formatDateToYMD(recruit.recruitStartDate),
            recruitEndDate: recruit.recruitEndDate ? formatDateToYMD(recruit.recruitEndDate) : '-',
            recruitStatus: isOngoing ? '진행중' : '진행 완료',
            isPublished: recruit.isPublished ? '✅' : '❌',
            originalRecruit: recruit,
        };
    });

    const handleRowClick = (params: GridRowParams): void => {
        const matchedRecruit = params.row.originalRecruit;
        if (matchedRecruit) {
            navigate('/board/recruit/detail', { 
                state: { recruit: matchedRecruit, recruitTypeOptions }
            });
        }
    };

    return (
        <div
        style={{
            width: '100%',
            minHeight: `calc(100vh - ${MAIN_RIGHT_TOP_HEIGHT})`,
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        <VerticalLayout
            height="auto"
            paddingLeft={36}
            paddingRight={36}
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',       // FIX: 안전하게 보이도록
                minWidth: 0,               // FIX: 자식 가로 수축 허용
                minHeight: 0,
            }}
        >
            <TitleLargeMedium>채용공고 게시판</TitleLargeMedium>
            <BTBox height="36px" />

            {/* ✅ 리스트 래퍼: 여기서 스크롤을 담당 */}
            <div
                style={{ width: '100%' }}
            >
                <BTList
                    rows={rows}
                    columns={columns}
                    checkboxSelection={true}
                    handleRowClick={handleRowClick}
                    onWriteButtonClick={handleWriteClick}
                    onDeleteButtonClick={handleDeleteClick}
                    writeButtonText={"추가"}
                    deleteButtonText={"삭제"}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    filterTitle="채용 형태"
                    filterOptions={[{ label: '전체', value: 'all' }, ...recruitTypeOptions]}
                    filterValue={selectedJobType}
                    onFilterChange={setSelectedJobType}
                    style={{ flex: 1, minHeight: 0, width: '100%' }} // ★ 유지
                    pageStateKey="board.recruit.list"
                />
            </div>
        </VerticalLayout>
    </div>
    );
});